import React from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { BadgeCheck, Users, Clock3, Wrench } from "lucide-react";

const STATS = [
  { icon: BadgeCheck, value: 10, suffix: "+", label: "Años de experiencia certificada" },
  { icon: Wrench, value: 850, suffix: "+", label: "Instalaciones y reparaciones ejecutadas" },
  { icon: Users, value: 60, suffix: "+", label: "Comunidades atendidas en Gran Canaria" },
  { icon: Clock3, value: 6, suffix: "h", label: "Tiempo medio de respuesta en urgencias" },
];

const Counter = ({ to, suffix }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const val = useMotionValue(0);
  const display = useTransform(val, (v) => Math.floor(v).toString());
  React.useEffect(() => {
    if (inView) {
      const controls = animate(val, to, { duration: 1.6, ease: [0.2, 0.8, 0.2, 1] });
      return () => controls.stop();
    }
  }, [inView, to, val]);
  return (
    <span ref={ref} className="counter-num font-display text-6xl sm:text-7xl leading-none">
      <motion.span>{display}</motion.span>
      <span className="text-[#E57C23]">{suffix}</span>
    </span>
  );
};

export default function WhyChooseMe() {
  return (
    <section id="sobre" className="relative py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="section-label mb-4">05 · Por qué elegirme</span>
            <h2 className="font-display text-4xl sm:text-5xl text-white mt-4 leading-[0.95]">
              Un mismo técnico, <span className="text-[#00E5FF]">de principio a fin.</span>
            </h2>
            <p className="mt-5 text-[#9CA3AF] leading-relaxed">
              Me llamo <span className="text-white">Francisco Moisés Castellano
              Monzón</span>, autónomo con más de una década especializado en
              baja tensión. Sin intermediarios, sin comerciales,
              sin sorpresas en la factura. Solo trabajo eléctrico bien hecho,
              con boletín cuando toca y con la garantía de que quien pisa tu
              casa es siempre el mismo.
            </p>

            <ul className="mt-8 space-y-3 text-[15px]">
              {[
                "Certificación oficial de instalador autorizado (REBT)",
                "Trabajo con material homologado y garantía escrita",
                "Presupuestos claros, sin cargos ocultos",
                "Atención directa por WhatsApp y móvil, sin call center",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-white/85">
                  <span className="mt-2 inline-block h-1.5 w-4 rounded-full bg-gradient-to-r from-[#E57C23] to-[#00E5FF]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-5">
            {STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  data-testid={`stat-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="card-circuit p-7 min-h-[220px] flex flex-col justify-between"
                >
                  <Icon size={20} className="text-[#E57C23] card-icon" strokeWidth={1.8} />
                  <div>
                    <Counter to={s.value} suffix={s.suffix} />
                    <div className="mt-3 text-[14px] text-white/60 leading-snug max-w-[220px]">
                      {s.label}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
